import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

function SessionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${API_URL}/api/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load sessions');
        return r.json();
      })
      .then((data) => setSessions(data))
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    setDeletingSessionId(sessionId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete session');
      }

      setSessions(sessions.filter((s) => s.id !== sessionId));
    } catch (err) {
      alert('Error deleting session: ' + err.message);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const filtered = sessions.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.child_name.toLowerCase().includes(q) ||
      s.child_mobile_number?.toLowerCase().includes(q) ||
      s.hospital_name?.toLowerCase().includes(q) ||
      s.hospital_location?.toLowerCase().includes(q) ||
      s.practitioner_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="home-shell" style={{ paddingTop: '90px' }}>

      {/* Header */}
      <header className="home-header">
        <div className="home-brand">
          <span
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', fontSize: '20px', marginRight: '4px' }}
          >←</span>
          <span className="home-logo-icon">📋</span>
          <span className="home-brand-name">
            {isAdmin ? 'All Sessions' : 'My Sessions'}
          </span>
        </div>
        <button onClick={() => navigate('/')} style={s.homeBtn}>🏠 Home</button>
      </header>

      {/* Content wrapper */}
      <div style={{ width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Stats + Search bar */}
        {!isLoading && !error && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Stats */}
            <div style={s.statPill}>
              🧒 <strong>{sessions.length}</strong> total session{sessions.length !== 1 ? 's' : ''}
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search by child name, mobile, hospital, practitioner…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={s.searchInput}
            />
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <p style={{ color: '#6B7280', fontSize: '16px' }}>⏳ Loading sessions…</p>
        )}

        {/* Error */}
        {error && (
          <div style={s.errorBox}>⚠️ {error}</div>
        )}

        {/* Empty */}
        {!isLoading && !error && filtered.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center', color: '#9CA3AF', padding: '48px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
            <p style={{ margin: 0, fontSize: '15px' }}>
              {search ? 'No sessions match your search.' : 'No sessions recorded yet.'}
            </p>
          </div>
        )}

        {/* Sessions list */}
        {filtered.map((session) => (
          <div key={session.id} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>

              {/* Left info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '22px' }}>🧒</span>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: '#1A2340' }}>
                    {session.child_name}
                  </span>
                  <span style={s.ageBadge}>{session.child_age} yrs</span>
                </div>

                <div style={s.metaRow}>
                  <span>📱</span>
                  <span>{session.child_mobile_number || 'N/A'}</span>
                </div>

                <div style={s.metaRow}>
                  <span>🏥</span>
                  <span>{session.hospital_name} — {session.hospital_location}</span>
                </div>

                {isAdmin && (
                  <div style={s.metaRow}>
                    <span>👨‍⚕️</span>
                    <span>Practitioner: <strong>{session.practitioner_name}</strong></span>
                  </div>
                )}

                <div style={s.metaRow}>
                  <span>🗓️</span>
                  <span>{formatDate(session.created_at)}</span>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate('/emotion-corner', {
                    state: {
                      sessionId: session.id,
                      childName: session.child_name,
                      childAge: session.child_age,
                      hospitalLocation: `${session.hospital_name} — ${session.hospital_location}`,
                      childMobileNumber: session.child_mobile_number,
                    },
                  })}
                  style={s.continueBtn}
                >
                  ▶ Continue
                </button>
                <button
                  onClick={() => navigate(`/report/${session.id}`)}
                  style={s.reportBtn}
                >
                  📊 View Report
                </button>

                {/* Delete button (admin only) */}
                {isAdmin && (
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    disabled={deletingSessionId === session.id}
                    style={{
                      ...s.deleteBtn,
                      opacity: deletingSessionId === session.id ? 0.5 : 1,
                      cursor: deletingSessionId === session.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {deletingSessionId === session.id ? '⏳ Deleting...' : '🗑️ Delete'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  homeBtn: {
    background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '12px',
    padding: '8px 16px', fontSize: '14px', fontWeight: '700', color: '#2563EB',
    cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  card: {
    background: '#fff', borderRadius: '16px', padding: '24px 28px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB',
  },
  errorBox: {
    background: '#FFF5F5', border: '2px solid #FCA5A5', borderRadius: '12px',
    padding: '14px 20px', fontSize: '14px', fontWeight: '600', color: '#991B1B',
  },
  statPill: {
    background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: '99px',
    padding: '6px 16px', fontSize: '14px', fontWeight: '600', color: '#1E40AF',
    whiteSpace: 'nowrap',
  },
  searchInput: {
    flex: 1, minWidth: '200px', padding: '9px 16px', fontSize: '14px',
    border: '1.5px solid #D1D5DB', borderRadius: '12px',
    fontFamily: 'Segoe UI, Inter, sans-serif', outline: 'none',
  },
  ageBadge: {
    background: '#F0FDF4', border: '1.5px solid #86EFAC', borderRadius: '99px',
    padding: '2px 10px', fontSize: '13px', fontWeight: '700', color: '#166534',
  },
  metaRow: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '14px', color: '#6B7280', fontWeight: '500',
  },
  continueBtn: {
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    border: 'none', borderRadius: '12px', padding: '10px 20px',
    fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif', whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
    alignSelf: 'center',
  },
  reportBtn: {
    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    border: 'none', borderRadius: '12px', padding: '10px 20px',
    fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif', whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
    alignSelf: 'center',
  },
  deleteBtn: {
    background: '#FEE2E2',
    border: '1.5px solid #FECACA', borderRadius: '12px',
    padding: '10px 16px', fontSize: '14px', fontWeight: '700', color: '#991B1B',
    cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif',
    transition: 'all 0.2s ease', whiteSpace: 'nowrap',
  },
};

export default SessionsPage;
